import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type VerticalKind = Database['public']['Enums']['vertical_kind'];

interface VerticalContextType {
 vertical: VerticalKind | null;
 setVertical: (vertical: VerticalKind) => Promise<void>;
 loading: boolean;
}

const VerticalContext = createContext<VerticalContextType>({
 vertical: null,
 setVertical: async () => {},
 loading: true,
});

export function VerticalProvider({ children }: { children: React.ReactNode }) {
 const [vertical, setVerticalState] = useState<VerticalKind | null>(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const loadUserVertical = async () => {
 try {
 const { data: { user } } = await supabase.auth.getUser();
 
 if (!user) {
 setLoading(false);
 return;
 }

 const { data } = await supabase
 .from('profile_with_role')
 .select('default_vertical')
 .eq('user_id', user.id)
 .maybeSingle();

 if (data?.default_vertical) {
 setVerticalState(data.default_vertical);
 } else {
 // User doesn't have a vertical - log warning
 console.warn('User has no default vertical set. Content may not display correctly.');
 }
 } catch (error) {
 console.error('Error loading user vertical:', error);
 } finally {
 setLoading(false);
 }
 };

 loadUserVertical();
 }, []);

 const setVertical = useCallback(async (newVertical: VerticalKind) => {
 try {
 setVerticalState(newVertical);
 
 const { data: { user } } = await supabase.auth.getUser();
 
 if (!user) {
 console.error('No authenticated user found');
 return;
 }

 const { error } = await supabase
 .from('profiles')
 .update({ default_vertical: newVertical })
 .eq('user_id', user.id);

 if (error) {
 console.error('Error saving vertical preference:', error);
 }
 } catch (error) {
 console.error('Error setting vertical:', error);
 }
 }, []);

 return (
 <VerticalContext.Provider value={{ vertical, setVertical, loading }}>
 {children}
 </VerticalContext.Provider>
 );
}

export const useVertical = () => {
 const context = useContext(VerticalContext);
 if (!context) {
 throw new Error('useVertical must be used within a VerticalProvider');
 }
 return context;
};